import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useEffect, useRef } from 'react';

import PriceTierPicker from '../../components/PriceTierPicker/PriceTierPicker';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { isContentSubscriptionListing } from '../../util/digitalSubscription';
import { autoPickTier, isTierEligible, normalizePriceTiers } from '../../util/priceTiers';
import { propTypes } from '../../util/types';
import { usePriceTierUnits } from '../../util/usePriceTierUnits';
import { mergeSoldOutTierUnits } from './CheckoutPageTransactionHelpers';

import css from './CheckoutPage.module.css';

/**
 * Checkout always lists the listing's price options so the buyer can switch
 * before paying. The selected tier is what speculate and initiate price.
 */
const CheckoutTierRecap = ({
  listing,
  orderData,
  intl,
  onSelectTier,
  unitsRefreshKey,
  soldOutTierIds,
  onAvailabilityChange,
  holdAutoPick,
}) => {
  const subscriptionListing = isContentSubscriptionListing(listing);
  const catalog = subscriptionListing
    ? null
    : normalizePriceTiers(listing?.attributes?.publicData?.priceTiers);
  const buyerChoseTier = useRef(false);
  const lastAutoPickedTierId = useRef(null);
  const bookingStart = orderData?.bookingDates?.bookingStart || orderData?.bookingStart || null;
  const { unitsSoldByTierId, unitsStatus, unitsAvailabilityKnown } = usePriceTierUnits({
    listingId: listing?.id?.uuid,
    slotStart: bookingStart,
    enabled: !!catalog,
    refreshKey: unitsRefreshKey,
  });

  const mergedUnitsSoldByTierId = mergeSoldOutTierUnits(unitsSoldByTierId, soldOutTierIds, catalog);
  const tierContext = {
    now: new Date(),
    bookingStart,
    listingType: listing?.attributes?.publicData?.listingType,
    publishedAt: listing?.attributes?.createdAt || null,
    unitsSoldByTierId: unitsAvailabilityKnown
      ? mergedUnitsSoldByTierId
      : mergeSoldOutTierUnits({}, soldOutTierIds, catalog),
  };
  const cheapestTier = catalog ? autoPickTier(catalog.items, tierContext) : null;
  const cheapestTierId = cheapestTier?.id || null;
  const currentTier = catalog?.items.find(item => item.id === orderData?.tierId);
  const storedEligible = !!(currentTier && isTierEligible(currentTier, tierContext));
  const keepBuyerChoice = storedEligible && (buyerChoseTier.current || !!orderData?.tierId);
  const hasEligibleTier = !!(cheapestTierId || keepBuyerChoice);
  const selectedTierId = keepBuyerChoice
    ? orderData.tierId
    : holdAutoPick
    ? null
    : cheapestTierId || null;

  useEffect(() => {
    if (typeof onAvailabilityChange === 'function') {
      onAvailabilityChange({ hasEligibleTier, unitsStatus });
    }
  }, [hasEligibleTier, unitsStatus, onAvailabilityChange]);

  useEffect(() => {
    if (holdAutoPick || keepBuyerChoice) {
      return;
    }
    if (!cheapestTierId || typeof onSelectTier !== 'function') {
      return;
    }
    if (cheapestTierId === orderData?.tierId) {
      lastAutoPickedTierId.current = cheapestTierId;
      return;
    }
    if (lastAutoPickedTierId.current === cheapestTierId) {
      return;
    }
    lastAutoPickedTierId.current = cheapestTierId;
    onSelectTier(cheapestTierId);
  }, [cheapestTierId, keepBuyerChoice, holdAutoPick, orderData?.tierId, onSelectTier]);

  const handleSelectTier = tierId => {
    buyerChoseTier.current = true;
    if (typeof onSelectTier === 'function') {
      onSelectTier(tierId);
    }
  };

  if (!catalog) {
    return null;
  }

  return (
    <div className={css.packageRecap} data-testid="checkout-tier-recap">
      {unitsStatus === 'error' ? (
        <p className={css.unitsUnavailable} data-testid="price-tier-units-unavailable">
          <FormattedMessage id="CheckoutPage.priceTierUnitsUnavailable" />
        </p>
      ) : null}
      <PriceTierPicker
        listing={listing}
        selectedTierId={selectedTierId}
        onSelect={handleSelectTier}
        intl={intl}
        bookingStart={bookingStart}
        unitsSoldByTierId={mergedUnitsSoldByTierId}
        unitsAvailabilityKnown={unitsAvailabilityKnown}
      />
    </div>
  );
};

CheckoutTierRecap.defaultProps = {
  listing: null,
  orderData: null,
  onSelectTier: null,
  unitsRefreshKey: null,
  soldOutTierIds: null,
  onAvailabilityChange: null,
  holdAutoPick: false,
};

CheckoutTierRecap.propTypes = {
  listing: propTypes.listing,
  orderData: object,
  intl: intlShape.isRequired,
  onSelectTier: func,
  unitsRefreshKey: string,
  soldOutTierIds: arrayOf(string),
  onAvailabilityChange: func,
  holdAutoPick: bool,
  config: object,
  className: string,
};

export default CheckoutTierRecap;
